'use client'

import type { PaginationProps } from 'antd'
import { Col, Empty, Pagination, Row, Spin, theme } from 'antd'
import React, {
  CSSProperties,
  Key,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react'
import styled, { css } from 'styled-components'

/**
 * Drop-in replacement for the deprecated antd `List` / `List.Item` /
 * `List.Item.Meta`. Keeps the subset of the antd API used in this codebase
 * (dataSource/renderItem, pagination, grid, loading, header/footer, split,
 * bordered, children) and replicates antd's pagination slicing so both
 * client-side and server-side (one page + total) sources behave identically.
 */

interface GridConfig {
  gutter?: number
  column?: number
}

const DataListContext = createContext<{
  split: boolean
  colorSplit: string
  grid: boolean
}>({
  split: true,
  colorSplit: 'rgba(5, 5, 5, 0.06)',
  grid: false,
})

interface DataListProps<T> {
  dataSource?: T[]
  renderItem?: (item: T, index: number) => ReactNode
  rowKey?: string | ((item: T) => Key)
  pagination?: false | (PaginationProps & { total?: number })
  grid?: GridConfig
  loading?: boolean
  header?: ReactNode
  footer?: ReactNode
  split?: boolean
  bordered?: boolean
  loadMore?: ReactNode
  locale?: { emptyText?: ReactNode }
  style?: CSSProperties
  className?: string
  children?: ReactNode
}

const ItemsWrap = styled.div<{ $split: boolean; $color: string; $padded: boolean }>`
  ${(p) => p.$padded && css`
    padding-inline: 24px;
  `}
  ${(p) => p.$split && css`
    > .data-list-item:not(:last-child) {
      border-block-end: 1px solid ${p.$color};
    }
  `}
`

export function DataList<T>(props: DataListProps<T>) {
  const { token } = theme.useToken()
  const {
    dataSource = [],
    renderItem,
    rowKey,
    pagination,
    grid,
    loading,
    header,
    footer,
    split = true,
    bordered = false,
    loadMore,
    locale,
    style,
    className,
    children,
  } = props

  const [internalCurrent, setInternalCurrent] = useState(
    (pagination && pagination.defaultCurrent) || 1,
  )
  const [internalPageSize, setInternalPageSize] = useState(
    (pagination && (pagination.defaultPageSize || pagination.pageSize)) || 10,
  )

  const current = pagination ? pagination.current ?? internalCurrent : 1
  const pageSize = pagination
    ? pagination.pageSize ?? internalPageSize
    : dataSource.length

  const keyOf = (item: T, index: number): Key => {
    if (typeof rowKey === 'function') return rowKey(item)
    if (typeof rowKey === 'string') return (item as any)?.[rowKey] ?? index
    return (item as any)?.key ?? index
  }

  // antd only slices when the offset is inside the provided dataSource; a
  // server-paginated source (one page + a larger `total`) is left untouched.
  let items = dataSource
  if (pagination && dataSource.length > (current - 1) * pageSize) {
    items = dataSource.slice(
      (current - 1) * pageSize,
      (current - 1) * pageSize + pageSize,
    )
  }

  const isEmpty = items.length === 0 && !children

  const body = grid ? (
    <Row gutter={grid.gutter}>
      {items.map((item, index) => (
        <Col key={keyOf(item, index)} span={24 / (grid.column || 4)}>
          {renderItem?.(item, index)}
        </Col>
      ))}
    </Row>
  ) : (
    items.map((item, index) => (
      <React.Fragment key={keyOf(item, index)}>
        {renderItem?.(item, index)}
      </React.Fragment>
    ))
  )

  const paginationNode =
    pagination && dataSource.length > 0 ? (
      <Pagination
        align="end"
        {...pagination}
        current={current}
        pageSize={pageSize}
        style={{ marginTop: 24, ...(pagination.style || {}) }}
        onChange={(page, size) => {
          setInternalCurrent(page)
          setInternalPageSize(size)
          pagination.onChange?.(page, size)
        }}
      />
    ) : null

  const sectionStyle = (border: 'top' | 'bottom'): CSSProperties => ({
    padding: bordered ? '12px 24px' : '12px 0',
    [`border${border === 'top' ? 'Top' : 'Bottom'}`]: `1px solid ${token.colorSplit}`,
  })

  return (
    <div
      className={className}
      style={{
        ...(bordered
          ? {
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
            }
          : {}),
        ...style,
      }}
    >
      {header && <div style={sectionStyle('bottom')}>{header}</div>}
      <Spin spinning={!!loading}>
        <DataListContext.Provider
          value={{ split, colorSplit: token.colorSplit, grid: !!grid }}
        >
          {isEmpty ? (
            <div style={{ padding: '16px 0' }}>
              {locale?.emptyText ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </div>
          ) : (
            <ItemsWrap $split={split && !grid} $color={token.colorSplit} $padded={bordered}>
              {body}
              {children}
            </ItemsWrap>
          )}
        </DataListContext.Provider>
      </Spin>
      {loadMore}
      {footer && <div style={sectionStyle('top')}>{footer}</div>}
      {paginationNode}
    </div>
  )
}

interface DataListItemProps {
  children?: ReactNode
  extra?: ReactNode
  actions?: ReactNode[]
  style?: CSSProperties
  className?: string
}

const ItemActions = styled.ul`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
`

export function DataListItem(props: DataListItemProps) {
  const { children, extra, actions, style, className } = props
  const { grid } = useContext(DataListContext)

  // In grid mode antd renders the item as a block filling its column. Keeping
  // the flex layout here would turn the cell content into a flex item that
  // cannot shrink below its max-content width (e.g. a Card with a long,
  // non-wrapping title), which blows up the surrounding layout.
  return (
    <div
      className={['data-list-item', className].filter(Boolean).join(' ')}
      style={{
        ...(grid
          ? { display: 'block', maxWidth: '100%', paddingBottom: 12 }
          : {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              padding: '12px 0',
            }),
        ...style,
      }}
    >
      <div
        style={
          grid
            ? { minWidth: 0 }
            : { display: 'flex', flex: 1, minWidth: 0, alignItems: 'center' }
        }
      >
        {children}
      </div>
      {actions && actions.length > 0 && (
        <ItemActions>
          {actions.map((action, index) => (
            <li key={`action-${index}`}>{action}</li>
          ))}
        </ItemActions>
      )}
      {extra}
    </div>
  )
}

interface DataListItemMetaProps {
  avatar?: ReactNode
  title?: ReactNode
  description?: ReactNode
  style?: CSSProperties
}

export function DataListItemMeta(props: DataListItemMetaProps) {
  const { token } = theme.useToken()
  const { avatar, title, description, style } = props

  return (
    <div style={{ display: 'flex', flex: 1, minWidth: 0, alignItems: 'flex-start', ...style }}>
      {avatar && <div style={{ marginInlineEnd: 16 }}>{avatar}</div>}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{ marginBottom: 4, color: token.colorText, fontSize: token.fontSize }}>
            {title}
          </div>
        )}
        {description && (
          <div style={{ color: token.colorTextDescription, fontSize: token.fontSize }}>
            {description}
          </div>
        )}
      </div>
    </div>
  )
}

// Convenience namespace so call-sites can mirror the antd `List.Item` shape.
DataList.Item = DataListItem as typeof DataListItem & { Meta: typeof DataListItemMeta }
DataList.Item.Meta = DataListItemMeta

// keep the context export usable if an item ever needs split awareness
export { DataListContext }
